import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, WS_BASE, POLL_MS, WS_RECONNECT_INITIAL, WS_RECONNECT_MAX, WS_PING_INTERVAL } from '../constants';

// FAVOLA emits PASS and KUNBI emits TAKE as their "take the trade" verb. In the
// cross-strategy feed we surface both as TRADE so they read consistently with
// every other system. BLOCK_* / EXIT_SIGNAL / SKIP are left untouched. The
// dedicated FAVOLA / KUNBI Monitor pages keep their native verb (PASS / TAKE) so
// their own counters, filters and highlights stay intact.
function normalizeDecision(d) {
  const a = d && (d.action_label || d.action);
  if (a === 'PASS' || a === 'TAKE') {
    return { ...d, action: 'TRADE', action_label: 'TRADE' };
  }
  return d;
}

// KUNBI records its exit ON THE SAME row (exit_time/exit_reason/exit_price) rather
// than emitting a separate EXIT decision like EMEKA/TIER1/NCHE. So the feed/CSV
// only ever showed KUNBI's entry, making a closed KUNBI trade look still-open next
// to a later entry. Split a closed KUNBI row into [entry, EXIT-event] so the exit
// shows as its own event, time-ordered (e.g. KUNBI exit 08:44 before TIER1 08:51).
function expandKunbiExit(d) {
  const act = d && (d.action || d.action_label);
  if (d && d.system_id === 'KUNBI' && act === 'TAKE' && d.exit_time) {
    const exitEvt = {
      ...d,
      id: `${d.id}-exit`,
      action: 'EXIT', action_label: 'EXIT',
      signal_id: `KUNBI-EXIT-${d.signal_id || d.id}`,
      signal_ts: d.exit_time, bar_time: d.exit_time, received_at: d.exit_time,
      reason: d.exit_reason || 'exit',
      entry_price: (d.exit_price != null ? d.exit_price : d.entry_price),
    };
    return [d, exitEvt];
  }
  return [d];
}

// Verdicts that must NOT surface in the cross-strategy trader feed (it drives
// the feed list, the featured card, and toasts): BLOCK / BLOCK_* (rejections),
// OBSERVE (log-only observations — traders must not act on them, e.g. a
// Phase-0 EMEKA QUIETSHORT), and IGNORED (NCHE orphan covers — a cover webhook
// with no open position; diagnostic only, not an actionable signal). Dedicated
// per-strategy Monitor pages still show them for ops; only this shared trader
// feed filters them out.
function isHiddenFromFeed(d) {
  const a = (d && (d.action_label || d.action)) || '';
  // BLOCK / BLOCK_* (rejections), OBSERVE (log-only), IGNORED (orphan covers),
  // SKIP (Kunbi filter/slot/C-skip rejection) — none are actionable, so they
  // stay off the cross-strategy feed (the per-strategy Monitor still shows them).
  // NB: ISINMI/MARS SKIPs are intentionally hidden too — the homepage feed shows
  // actionable trades only; the sequencing systems surface when they TAKE.
  return a.startsWith('BLOCK') || a === 'OBSERVE' || a === 'IGNORED' || a === 'SKIP'
      || a === 'SHADOW';   // WURA open_drive shadow rows are log-only, not actionable
}

export default function useFeed(feedConfig) {
  const [decisions, setDecisions] = useState([]);
  const [regime, setRegime]       = useState(null);
  const [perSystem, setPerSystem] = useState(null);
  const [online, setOnline]       = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastPoll, setLastPoll]   = useState(null);
  const [loading, setLoading]     = useState(true);

  // Tracks whether the most recent state update came from a WS push so the
  // next HTTP poll can decide whether to trust an empty response (a wipe)
  // or treat it as a race against an in-flight DB commit (keep what we have).
  const lastWsAtRef = useRef(0);

  const mergeDecision = useCallback((raw) => {
    if (!raw || !raw.signal_ts) return;
    if (isHiddenFromFeed(raw)) return; // Hide BLOCK / BLOCK_* + OBSERVE — actionable signals only
    const d = normalizeDecision(raw); // FAVOLA PASS -> TRADE for the cross-strategy feed
    lastWsAtRef.current = Date.now();
    setDecisions(prev => {
      // Dedup key includes signal_id so two distinct Pine alerts that fire on
      // the same bar (e.g. Trident-exit + Cobra-exit both at 10:21) don't
      // collide and clobber each other. Falls back to (system_id, signal_ts)
      // for strategies that don't carry signal_id (e.g. P1-S1).
      const key = `${d.system_id}-${d.signal_ts}-${d.signal_id || ''}`;
      const without = prev.filter(x => `${x.system_id}-${x.signal_ts}-${x.signal_id || ''}` !== key);
      return [d, ...without].slice(0, 100);
    });
    // Re-sync from HTTP shortly after so sidebar/stats reflect authoritative
    // server state instead of waiting up to POLL_MS for the next tick.
    // 250ms gives a slow DB commit time to land before we re-query.
    setTimeout(() => fetchDecisionsRef.current?.(), 250);
  }, []);

  // Stable callable to avoid putting fetchDecisions in mergeDecision's deps
  // (which would re-create the callback on every render of the parent).
  const fetchDecisionsRef = useRef(null);

  const fetchDecisions = useCallback(async () => {
    const extraPaths = feedConfig.extraRestPaths || [];
    try {
      // Fetch a generous window so the BLOCK filter below doesn't strand a
      // strategy's TRADE rows behind a wall of recent BLOCKs (see Portfolio
      // bug 2026-06-03: GBEMI/STRATA had 50+ consecutive BLOCKs that pushed
      // their last real TRADEs past limit=50).
      const FETCH_LIMIT = 200;
      const [primaryRes, ...extraRes] = await Promise.all([
        fetch(`${API_BASE}${feedConfig.restPath}?limit=${FETCH_LIMIT}`, { signal: AbortSignal.timeout(8000) }),
        ...extraPaths.map(p => fetch(`${API_BASE}${p}?limit=${FETCH_LIMIT}`, { signal: AbortSignal.timeout(8000) })),
      ]);
      if (!primaryRes.ok) throw new Error(`HTTP ${primaryRes.status}`);
      const primaryData = await primaryRes.json();

      const extraData = await Promise.all(
        extraRes.map(r => r.ok ? r.json() : Promise.resolve({ decisions: [] }))
      );

      // Drop rows older than 14 days so the dashboard stays focused on
      // recent activity. Low-frequency strategies (e.g. NQ-PB-M in Phase A)
      // would otherwise leave stale month-old rows at the tail of the buffer.
      const RECENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
      const cutoff = Date.now() - RECENT_WINDOW_MS;

      const seen = new Set();
      const merged = [...(primaryData.decisions || []), ...extraData.flatMap(d => d.decisions || [])]
        .flatMap(expandKunbiExit) // KUNBI: surface its on-row exit as its own EXIT event
        .map(normalizeDecision) // FAVOLA PASS -> TRADE for the cross-strategy feed
        .filter(d => !isHiddenFromFeed(d)) // Hide BLOCK / BLOCK_* + OBSERVE — actionable signals only
        .filter(d => {
          const ts = d.signal_ts ? Date.parse(d.signal_ts) : NaN;
          return Number.isFinite(ts) && ts >= cutoff;
        })
        .filter(d => {
          // Same dedup key as the WS merge above — include signal_id so two
          // distinct alerts at the same (system, ts) don't drop each other.
          const k = `${d.system_id}-${d.signal_ts}-${d.signal_id || ''}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        })
        .sort((a, b) => new Date(b.signal_ts) - new Date(a.signal_ts))
        .slice(0, 100);

      const mergedPerSystem = extraData.reduce((acc, d) => {
        if (d.per_system) Object.assign(acc, d.per_system);
        return acc;
      }, { ...(primaryData.per_system || {}) });

      setDecisions(prev => {
        // Race protection: if this poll returns nothing AND we just received
        // a WS push within the last 5s, the empty response is most likely
        // racing an in-flight DB commit. Keep the WS-delivered state. The
        // next poll will re-confirm — if rows are genuinely gone (e.g. an
        // admin wipe), state goes empty after the second consecutive empty
        // poll instead of pinging back and forth.
        if (merged.length === 0 && prev.length > 0
            && Date.now() - lastWsAtRef.current < 5000) {
          return prev;
        }
        return merged;
      });
      setRegime(primaryData.regime || null);
      setPerSystem(Object.keys(mergedPerSystem).length ? mergedPerSystem : null);
      setOnline(true);
      setLastPoll(new Date());
    } catch {
      setOnline(false);
    } finally {
      setLoading(false);
    }
  }, [feedConfig.restPath, feedConfig.extraRestPaths]);

  // Keep the ref pointed at the latest fetchDecisions so mergeDecision can
  // trigger a re-sync without taking fetchDecisions as a dependency (which
  // would force the WS effect to teardown/reconnect on every poll cycle).
  useEffect(() => {
    fetchDecisionsRef.current = fetchDecisions;
  }, [fetchDecisions]);

  useEffect(() => {
    setLoading(true);
    setDecisions([]);
    fetchDecisions();
    const interval = setInterval(fetchDecisions, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchDecisions]);

  useEffect(() => {
    if (!feedConfig.wsPath) {
      setWsConnected(false);
      return;
    }

    let ws, reconnectTimer, pingTimer;
    let delay = WS_RECONNECT_INITIAL;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(`${WS_BASE}${feedConfig.wsPath}`);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        setWsConnected(true);
        delay = WS_RECONNECT_INITIAL;
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch {}
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'decision.new' && msg.decision) mergeDecision(msg.decision);
        } catch {}
      };

      ws.onerror = () => { try { ws.close(); } catch {} };
      ws.onclose = () => {
        setWsConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      reconnectTimer = setTimeout(() => {
        delay = Math.min(delay * 2, WS_RECONNECT_MAX);
        connect();
      }, delay);
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (ws) { ws.onclose = null; try { ws.close(); } catch {} }
    };
  }, [feedConfig.wsPath, mergeDecision]);

  return { decisions, regime, perSystem, online, wsConnected, lastPoll, loading };
}
